import React, { useMemo } from 'react';
import { AutoComplete, Button, Form, Input, Space, Tag, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

// What a drug is usually prescribed at, in the order a prescription is read:
// the dose, then how often, then for how long. `field` is the prescription box
// the value fills in, `source` the field it is remembered in on the drug.
const USUAL_DOSE_FIELDS = [
    { field: 'dosage', source: 'defaultDosage', labelKey: 'dosage', color: 'blue' },
    { field: 'frequency', source: 'defaultFrequency', labelKey: 'frequency', color: 'purple' },
    { field: 'duration', source: 'defaultDuration', labelKey: 'duration', color: 'green' }
];

const usualDoses = (drug) => USUAL_DOSE_FIELDS
    .map((entry) => ({ ...entry, value: drug[entry.source] }))
    .filter((entry) => Boolean(entry.value));

// One row of the drug search. The dosage, frequency and duration this doctor
// gives the drug at are the point of it: a list of bare names cannot tell two
// similar drugs apart, and the doctor has to know which one they are picking
// before they pick it.
const DrugSearchOption = ({ drug, t }) => {
    const doses = usualDoses(drug);
    const subtitle = [drug.genericName, drug.strength, drug.dosageForm].filter(Boolean).join(' · ');

    return (
        <div style={{ padding: '2px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <Text strong>{drug.name}</Text>
                {subtitle && <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {doses.length > 0 ? doses.map(({ field, labelKey, color, value }) => (
                    // Each value is spelled out with the field it belongs to - colour
                    // alone would leave the doctor guessing which number is the dose
                    // and which is the course length.
                    <Tag key={field} color={color} style={{ fontSize: 11, marginInlineEnd: 0 }}>
                        {t(labelKey)}: {value}
                    </Tag>
                )) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('noUsualDose')}</Text>
                )}
            </div>
        </div>
    );
};

// The prescription rows of a visit form. Shared by the create- and edit-visit
// forms so the drug search behaves the same in both.
const PrescriptionFields = ({ form, drugs = [], listName = 'prescriptions' }) => {
    const { t } = useTranslation();

    const options = useMemo(() => drugs.map((drug) => ({
        value: drug.name,
        drug,
        // `label` is a node, so the text the filter runs over is carried here -
        // and it covers the generic name too, since that is often what the doctor
        // remembers when the brand name is not what they are typing.
        searchText: [drug.name, drug.genericName].filter(Boolean).join(' ').toLowerCase(),
        label: <DrugSearchOption drug={drug} t={t} />
    })), [drugs, t]);

    // Picking a drug carries the doses shown in the search into the row, so the
    // usual case is one pick instead of four boxes typed out. Only empty boxes are
    // filled: whatever the doctor already wrote on this row stands.
    const applyUsualDoses = (rowName) => (_value, option) => {
        const drug = option && option.drug;
        if (!drug) return;

        USUAL_DOSE_FIELDS.forEach(({ field, source }) => {
            if (!drug[source]) return;

            const path = [listName, rowName, field];
            if (form.getFieldValue(path)) return;
            form.setFieldValue(path, drug[source]);
        });
    };

    return (
        <Form.List name={listName}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                            <Form.Item
                                {...restField}
                                name={[name, 'drugName']}
                                rules={[{ required: true, message: t('drugName') + ' ' + t('required') }]}
                            >
                                <AutoComplete
                                    placeholder={t('selectOrTypeDrug')}
                                    style={{ width: 200 }}
                                    // The name alone fits 200px; the doses under it do not.
                                    popupMatchSelectWidth={380}
                                    options={options}
                                    filterOption={(inputValue, option) =>
                                        (option?.searchText || '').includes((inputValue || '').toLowerCase())
                                    }
                                    onSelect={applyUsualDoses(name)}
                                />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, 'dosage']}>
                                <Input placeholder={t('dosage')} style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, 'frequency']}>
                                <Input placeholder={t('frequency')} style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, 'duration']}>
                                <Input placeholder={t('duration')} style={{ width: 100 }} />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, 'notes']}>
                                <Input placeholder={t('notes')} style={{ width: 150 }} />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} />
                        </Space>
                    ))}
                    <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                            {t('addPrescription')}
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form.List>
    );
};

export default PrescriptionFields;
